import { CardContent, CardHeading, LabeledSwatch } from '@/components/ui'
import { useStatementCoverage } from '@/modules/projects'
import { getProjectPath } from '@/utils'
import { summarizeCoverage } from './coverage'
import { OverviewViewLink } from './OverviewViewLink'
import { ProcessingCard } from './ProcessingCard'

interface OverviewCoverageCardProps {
  slug: string
}

const COVERED_COLOR = '#10b981' // emerald-500
const GAP_COLOR = '#f59e0b' // amber-500

const RING_SIZE = 124
const RING_STROKE = 12
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

// Coverage ring: green for the covered share of account-months, amber for the gaps.
export const OverviewCoverageCard = ({ slug }: OverviewCoverageCardProps) => {
  const coverage = useStatementCoverage()
  const {
    covered,
    gaps,
    totalMonths,
    percent,
    incompleteAccounts,
    accountCount,
  } = summarizeCoverage(coverage)

  const coveredDash = (percent / 100) * RING_CIRCUMFERENCE
  const gapDash = RING_CIRCUMFERENCE - coveredDash

  return (
    <ProcessingCard className="flex flex-col">
      <CardHeading
        title="Coverage"
        actions={
          <OverviewViewLink
            to={`${getProjectPath({ slug })}/insights/coverage`}
          />
        }
      />
      <CardContent className="flex flex-1 items-start">
        {totalMonths === 0 ? (
          <p className="text-sm text-muted-foreground">
            Upload statements on the Files tab to see coverage.
          </p>
        ) : (
          <div className="flex w-full flex-col items-center gap-4">
            <div
              className="relative shrink-0"
              style={{ width: RING_SIZE, height: RING_SIZE }}
            >
              <svg
                width={RING_SIZE}
                height={RING_SIZE}
                role="img"
                aria-label={`${percent}% of account-months covered`}
              >
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={RING_STROKE}
                  className="text-neutral-100"
                />
                {gaps > 0 ? (
                  <circle
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_RADIUS}
                    fill="none"
                    stroke={GAP_COLOR}
                    strokeWidth={RING_STROKE}
                    strokeDasharray={`${gapDash} ${RING_CIRCUMFERENCE}`}
                    strokeDashoffset={-coveredDash}
                    transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                  />
                ) : null}
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  fill="none"
                  stroke={COVERED_COLOR}
                  strokeWidth={RING_STROKE}
                  strokeDasharray={`${coveredDash} ${RING_CIRCUMFERENCE}`}
                  transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-7 tabular-nums">
                  {percent}%
                </span>
              </div>
            </div>

            <div className="w-full space-y-2">
              <LabeledSwatch
                swatchClassName="bg-emerald-500"
                label="Covered"
                value={covered}
                className="gap-2 text-sm text-blue-6"
              />
              <LabeledSwatch
                swatchClassName="bg-amber-500"
                label="Gaps"
                value={gaps}
                className="gap-2 text-sm text-blue-6"
              />
              <p className="text-xs text-blue-4">
                {gaps === 0
                  ? `all ${totalMonths} account-months covered`
                  : `${gaps === 1 ? 'gap' : 'gaps'} in ${incompleteAccounts} of ${accountCount} accounts`}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </ProcessingCard>
  )
}
